import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import { type AccreditationFormMessages, EMAIL_MAX, PHONE_MAX, ACCREDITATION_LONG_TEXT_MAX, ACCREDITATION_TEXT_MAX, enAccreditationFormMessages } from "./accreditation-write";
import type { Medal } from "./competition/vocab";
import type { ShortlistRole } from "./shortlist";

/**
 * The participant's own pages (user, 2026-11-03): `/me/*`, active role
 * PARTICIPANT only. Everything is keyed on the login (`people.user_id`), so
 * there is nothing to pick and nobody else to see. No ID numbers, no status
 * notes, no internal ids beyond what a page needs.
 */

/** One of the person's cards, as the participant sees it. */
export interface ParticipantCard {
	ac_card_id: string;
	/** `REG-<org>-000123` while not approved, `ACC-<org>-000123` once approved. */
	card_no: string;
	status: AcCardStatus;
	games: { code: string; name: string };
	organization: { name: string; code: string | null };
	category: { name: string; code: string; card_color: string | null };
	sub_function: string | null;
	sport: string | null;
}

/** The contact fields a participant may edit on their own record. Nothing that decides accreditation. */
export const PARTICIPANT_CONTACT_FIELDS = [
	"email_address",
	"phone_country_code",
	"phone_number",
	"residential_address",
	"emergency_contact_name",
	"emergency_contact_relationship",
	"emergency_phone_country_code",
	"emergency_phone_number",
] as const;
export type ParticipantContactField = (typeof PARTICIPANT_CONTACT_FIELDS)[number];
export type ParticipantContact = Record<ParticipantContactField, string | null>;

export interface ParticipantProfile {
	printed_name: string;
	photo_url: string | null;
	/** The organisation / category of the person's main card (approved first, then the newest). */
	organization: { name: string; code: string | null } | null;
	category: { name: string; code: string; card_color: string | null } | null;
	/** Holds an APPROVED athlete (`Aa`) card: the events / medals pages apply. */
	is_athlete: boolean;
	cards: ParticipantCard[];
	contact: ParticipantContact;
	/** The record's change stamp: send it back with PATCH (→ 409 STALE_VERSION when it moved). */
	version: string;
}

/** One event the person is entered in or shortlisted for. */
export interface ParticipantEvent {
	event_id: string;
	event_name: string;
	event_code: string;
	sport: { code: string; name: string; icon_url: string | null };
	games: { code: string; name: string };
	/** PRIMARY / RESERVE on the shortlist; null when entered without a shortlist row (a team). */
	shortlist_role: ShortlistRole | null;
	/** On an OVR roster for this event. */
	entered: boolean;
	/** Their entry's meets (heats, matches, finals), in schedule order, at most `PARTICIPANT_EVENT_MEETS_MAX`. */
	meets: {
		meet_id: string;
		name: string;
		scheduled_start: string | null;
		venue: string | null;
		schedule_status: string;
		result_status: string;
		rank: number | null;
		result: string | null;
	}[];
	/** The first scheduled meet, for ordering. */
	first_start: string | null;
}

export interface ParticipantMedal {
	event_id: string;
	event_name: string;
	sport: { code: string; name: string; icon_url: string | null };
	games: { code: string; name: string };
	medal: Medal;
	awarded_at: string;
}

/** A person has few events; the list is still capped (and paged). */
export const PARTICIPANT_EVENTS_PAGE_MAX = 100;
export const PARTICIPANT_EVENTS_PAGE_DEFAULT = 50;
export const PARTICIPANT_EVENT_MEETS_MAX = 20;
/** A person's medals: bounded by their events; capped all the same. */
export const PARTICIPANT_MEDALS_MAX = 200;

export const participantEventsQuery = z.object({
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PARTICIPANT_EVENTS_PAGE_MAX).default(PARTICIPANT_EVENTS_PAGE_DEFAULT),
});

type M = AccreditationFormMessages;

const optionalText = (m: M, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();

/**
 * `PATCH /me/profile`: the contact fields (each optional; blank clears it)
 * and the `version` from the read. Trim first (bug 5).
 */
export function makeParticipantContactForm(m: M = enAccreditationFormMessages) {
	return z
		.object({
			version: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?Z$/, m.required),
			email_address: z
				.string()
				.trim()
				.max(EMAIL_MAX, m.textTooLong)
				.transform((v) => (v === "" ? null : v))
				.pipe(z.string().email(m.emailInvalid).nullable())
				.nullable()
				.optional(),
			phone_country_code: optionalText(m, 8),
			phone_number: optionalText(m, PHONE_MAX),
			residential_address: optionalText(m, ACCREDITATION_LONG_TEXT_MAX),
			emergency_contact_name: optionalText(m, ACCREDITATION_TEXT_MAX),
			emergency_contact_relationship: optionalText(m, ACCREDITATION_TEXT_MAX),
			emergency_phone_country_code: optionalText(m, 8),
			emergency_phone_number: optionalText(m, PHONE_MAX),
		})
		.strict();
}
export const participantContactBody = makeParticipantContactForm();
export type ParticipantContactBody = z.input<typeof participantContactBody>;

export interface ParticipantContactResult {
	changed: boolean;
	profile: ParticipantProfile;
}
